"use client";

import { Bell, Zap, Check, AlertCircle, Menu } from "lucide-react";
import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import Link from "next/link";
import { cn } from "@/lib/utils";

export function Topbar({ onMenuClick }: { onMenuClick?: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-border bg-card/95 backdrop-blur-md px-4 md:px-6 transition-colors">
      <div className="flex items-center gap-3 md:gap-6">
        <Button variant="ghost" size="icon" className="md:hidden shrink-0 h-9 w-9 rounded-lg" onClick={onMenuClick}>
          <Menu className="h-5 w-5" />
        </Button>
      </div>
      
      <div className="flex items-center gap-3">
        {/* Static Environment Status Indicator */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-semibold">
          <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-[11px] font-semibold uppercase tracking-wider">
            Live Mode
          </span>
        </div>

        <div className="h-5 w-[1px] bg-border mx-1" />

        {/* Notifications Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg relative hover:bg-muted/80 border border-border/60">
              <Bell className="h-4 w-4 text-muted-foreground" />
              <span className="absolute top-2 right-2 h-2 w-2 rounded-full bg-destructive shadow-sm animate-pulse"></span>
              <span className="sr-only">Notifications</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-80 md:w-96 bg-card border-border shadow-xl p-0" align="end" sideOffset={10}>
            <DropdownMenuGroup>
              <DropdownMenuLabel className="flex items-center justify-between p-3.5 border-b border-border bg-muted/20">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-foreground">Alert Center</span>
                </div>
                <Badge variant="destructive" className="text-[9px] h-4 font-bold uppercase tracking-widest px-1.5">
                  3 Unread
                </Badge>
              </DropdownMenuLabel>
              
              <div className="max-h-[340px] overflow-y-auto divide-y divide-border/60">
                {[
                  { title: "Payout Processed", time: "2m ago", desc: "₦450,000.00 successfully transferred to Tech Corp.", type: "success", icon: Check },
                  { title: "Low Balance Warning", time: "1h ago", desc: "Wallet balance is below ₦500,000 threshold.", type: "warning", icon: AlertCircle },
                  { title: "API Gateway Update", time: "5h ago", desc: "v2.4 endpoint enhancements are active.", type: "info", icon: Zap },
                ].map((note, i) => (
                  <DropdownMenuItem key={i} className="cursor-pointer p-3.5 focus:bg-muted/60 transition-colors">
                    <div className="flex gap-3 items-start w-full">
                      <div className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5 shadow-sm",
                        note.type === "success" ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20" :
                        note.type === "warning" ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20" : 
                        "bg-primary/10 text-primary border border-primary/20"
                      )}>
                        <note.icon className="h-4 w-4" />
                      </div>
                      <div className="flex flex-col gap-1 flex-1">
                        <div className="flex items-center justify-between w-full">
                          <span className="font-semibold text-xs text-foreground">{note.title}</span>
                          <span className="text-[10px] text-muted-foreground font-medium">{note.time}</span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed">{note.desc}</p>
                      </div>
                    </div>
                  </DropdownMenuItem>
                ))}
              </div>
              
              <DropdownMenuSeparator className="m-0" />
              <DropdownMenuItem asChild className="cursor-pointer justify-center p-3 hover:bg-muted/40 transition-colors">
                <Link href="/dashboard/notifications" className="text-xs font-semibold text-primary">
                  View All Activity Logs
                </Link>
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Theme Toggle */}
        <ModeToggle />
      </div>
    </header>
  );
}


